import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { formatDate } from '../utils/dateUtils';
import { useAuth } from '../context/AuthContext';
import { submissions, coins, certLookup, images } from '../utils/api';
import SubmissionModal from '../components/SubmissionModal';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';

export default function ViewSubmission() {
  const { id } = useParams();
  const { user } = useAuth();
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [editingSubmission, setEditingSubmission] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await submissions.delete(id);
      navigate('/submissions');
    } catch (err) {
      setError('Failed to delete submission');
      setDeleteLoading(false);
      setShowDeleteModal(false);
    }
  };

  const handleEdit = () => {
    if (!submission || !submission.coins) {
      console.error('Submission or coins data is missing');
      return;
    }

    // Make sure we pass a copy of the submission to avoid reference issues
    // and include grading_service_id which is required by the modal
    setEditingSubmission({
      ...submission,
      grading_service_id: submission.grading_service_id || '',
      coins: submission.coins.map(coin => ({...coin}))
    });
  };

  useEffect(() => {
    loadSubmission();
  }, [id]);

  const loadSubmission = async () => {
    try {
      const data = await submissions.getById(id);
      
      // Ensure we have all required fields
      setSubmission({
        ...data,
        grading_service_id: data.grading_service_id || '',
        coins: Array.isArray(data.coins) ? data.coins : []
      });
      setLoading(false);
    } catch (err) {
      console.error('Failed to load submission:', err);
      setError('Failed to load submission details');
      setLoading(false);
    }
  };

  const getTrackingNumber = (number) => {
    if (!number) return '-';
    const match = number.match(/[?&]trknbr=([^&]+)/);
    return match ? match[1] : number;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md bg-red-50 p-4 my-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm font-medium text-red-800">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!submission) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">Submission not found</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-5xl p-4">
      <div className="mb-4">
        <div className="flex justify-between items-center">
          <Link
            to="/submissions"
            className="text-indigo-600 hover:text-indigo-900 mb-2 inline-flex items-center"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Submissions
          </Link>
          <div className="flex gap-2">
            <button
              onClick={handleEdit}
              className="px-3 py-1 text-sm font-medium text-indigo-600 hover:text-indigo-900 border border-indigo-600 rounded hover:bg-indigo-50"
            >
              Edit
            </button>
            <button
              onClick={() => setShowDeleteModal(true)}
              className="px-3 py-1 text-sm font-medium text-red-600 hover:text-red-900 border border-red-600 rounded hover:bg-red-50"
              disabled={deleteLoading}
            >
              {deleteLoading ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
        <h1 className="text-2xl font-bold mt-2">Submission Details</h1>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        title="Delete Submission"
        message="Are you sure you want to delete this submission? This action cannot be undone and will return all coins to their previous status."
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteModal(false)}
      />

      {/* Edit Modal */}
      {editingSubmission && (
        <SubmissionModal
          initialData={editingSubmission}
          onClose={() => {
            setEditingSubmission(null);
            loadSubmission(); // Refresh the data after editing
          }}
        />
      )}

      {/* Coin Mismatch Warning */}
      {submission.coin_mismatch && (
        <div className="rounded-md bg-amber-50 border border-amber-200 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-amber-800">
                Coin Count Mismatch Detected
              </h3>
              <div className="mt-2 text-sm text-amber-700">
                <p className="mb-2">
                  The number of coins you selected ({submission.coin_mismatch.userCoinCount}) doesn't match 
                  the number of coins PCGS returned ({submission.coin_mismatch.pcgsCoinCount}).
                </p>
                
                {submission.coin_mismatch.unmatchedUserCoins?.length > 0 && (
                  <div className="mb-2">
                    <p className="font-medium">Coins in your submission not found in PCGS response:</p>
                    <ul className="list-disc list-inside ml-2">
                      {submission.coin_mismatch.unmatchedUserCoins.map((coin, idx) => (
                        <li key={idx}>
                          Line #{coin.lineNumber}: {coin.userCoinId}
                        </li>
                      ))}
                    </ul>
                    <p className="mt-1 text-xs">
                      These coins may need to be removed from this submission or their line numbers corrected.
                    </p>
                  </div>
                )}
                
                {submission.coin_mismatch.unmatchedPcgsCoins?.length > 0 && (
                  <div className="mb-2">
                    <p className="font-medium">Coins from PCGS not matched to your inventory:</p>
                    <ul className="list-disc list-inside ml-2">
                      {submission.coin_mismatch.unmatchedPcgsCoins.map((coin, idx) => (
                        <li key={idx}>
                          Line #{coin.lineNumber}: {coin.description || 'No description'} 
                          {coin.grade && ` - Grade: ${coin.grade}`}
                          {coin.certNumber && ` - Cert: ${coin.certNumber}`}
                        </li>
                      ))}
                    </ul>
                    <p className="mt-1 text-xs">
                      You may need to add these coins to your inventory and link them to this submission.
                    </p>
                  </div>
                )}
                
                <div className="mt-3">
                  <button
                    onClick={handleEdit}
                    className="inline-flex items-center px-3 py-1.5 border border-amber-600 text-sm font-medium rounded text-amber-700 bg-amber-100 hover:bg-amber-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500"
                  >
                    Edit Submission to Resolve
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Submission Details Card */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="px-4 py-3">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            <div>
              <h3 className="text-xs font-medium text-gray-500">TPG</h3>
              <p className="text-sm text-gray-900">{submission.grading_service_name}</p>
            </div>
            <div>
              <h3 className="text-xs font-medium text-gray-500">Submission #</h3>
              <p className="text-sm text-gray-900">{submission.submission_number || '-'}</p>
            </div>
            <div>
              <h3 className="text-xs font-medium text-gray-500">Order #</h3>
              <p className="text-sm text-gray-900">
                {submission.order_number ? (
                  <a 
                    href={`https://www.pcgs.com/myaccount/order-details/${submission.order_number}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-600 hover:text-indigo-900"
                  >
                    {submission.order_number}
                  </a>
                ) : '-'}
              </p>
            </div>
            <div>
              <h3 className="text-xs font-medium text-gray-500">Status</h3>
              <p>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                  submission.status === 'Arrived' ? 'bg-gray-100 text-gray-800' :
                  submission.status === 'Received' ? 'bg-orange-100 text-orange-800' :
                  submission.status === 'Grading' ? 'bg-blue-100 text-blue-800' :
                  submission.status === 'Encapsulation' ? 'bg-yellow-100 text-yellow-800' :
                  submission.status === 'Quality Check' ? 'bg-indigo-100 text-indigo-800' :
                  submission.status === 'Order Shipped' ? 'bg-purple-600 text-white' :
                  submission.status === 'Delivered' ? 'bg-green-100 text-green-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {submission.status}
                </span>
              </p>
            </div>
            <div>
              <h3 className="text-xs font-medium text-gray-500">Status Date</h3>
              <p className="text-sm text-gray-900">{submission.status_date ? formatDate(submission.status_date, { user, includeTime: true }) : '-'}</p>
            </div>
            <div>
              <h3 className="text-xs font-medium text-gray-500">Service Type</h3>
              <p className="text-sm text-gray-900">{submission.service_type || '-'}</p>
            </div>
            <div>
              <h3 className="text-xs font-medium text-gray-500">Date Received</h3>
              <p className="text-sm text-gray-900">{submission.date_received ? formatDate(submission.date_received, { user }) : '-'}</p>
            </div>
            <div>
              <h3 className="text-xs font-medium text-gray-500">Date Shipped</h3>
              <p className="text-sm text-gray-900">{submission.date_shipped ? formatDate(submission.date_shipped, { user }) : '-'}</p>
            </div>
            <div>
              <h3 className="text-xs font-medium text-gray-500">Tracking #</h3>
              <p className="text-sm text-gray-900">
                {submission.tracking_number ? (
                  submission.tracking_url ? (
                    <a 
                      href={submission.tracking_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-indigo-600 hover:text-indigo-900"
                    >
                      {getTrackingNumber(submission.tracking_number)}
                    </a>
                  ) : submission.tracking_number
                ) : '-'}
              </p>
            </div>
          </div>
          {/* Description Section */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <h3 className="text-xs font-medium text-gray-500">Description</h3>
            <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{submission.description || '-'}</p>
          </div>
        </div>
      </div>

      {/* Coins List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-4 py-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-medium text-gray-900">Submission Coins</h2>
          </div>
          {submission.coins && submission.coins.length > 0 ? (() => {
            // Sort coins by line number before mapping
            submission.coins.sort((a, b) => {
              const lineA = parseInt(a.line_number) || 0;
              const lineB = parseInt(b.line_number) || 0;
              return lineA - lineB;
            });
            return (
              <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Line #
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User Coin ID
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Details
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Grade
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cert #
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Images
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {submission.coins.map((coin, index) => (
                    <tr key={coin.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {coin.line_number || index + 1}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        <Link
                          to={`/coins/${coin.id}`}
                          className="text-indigo-600 hover:text-indigo-900"
                        >
                          {coin.user_coin_id}
                        </Link>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                        {coin.details || '-'}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {coin.grade ? (
                          coin.grade
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            Pending
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {coin.cert_number ? (
                          coin.cert_number
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            Pending
                          </span>
                        )}
                      </td>
                      <td className="px-3 whitespace-nowrap text-sm text-gray-900 bg-white">
                        {coin.images?.length > 0 ? (
                          <div className="flex gap-1">
                            {coin.images.map((image, i) => (
                              <div key={i} className="relative h-12 w-12 rounded overflow-hidden">
                                <img
                                  src={images.getImage(image.thumbnail_path, image.source)}
                                  alt={`Coin ${i + 1}`}
                                  className="absolute inset-0 w-full h-full object-contain cursor-pointer hover:opacity-75"
                                  onClick={() => setSelectedImage(image.file_path)}
                                />
                              </div>
                            ))}
                          </div>
                        ) : coin.cert_number ? (
                          <div className="flex gap-1">
                            <div className="relative h-12 w-12 rounded overflow-hidden">
                              <img
                                src={`https://images.pcgs.com/CoinFacts/${coin.cert_number}_max.jpg`}
                                alt={`PCGS Coin ${coin.cert_number}`}
                                className="absolute inset-0 w-full h-full object-contain cursor-pointer hover:opacity-75"
                                onClick={() => setSelectedImage(`https://images.pcgs.com/CoinFacts/${coin.cert_number}_max.jpg`)}
                                onError={(e) => {
                                  e.target.onerror = null; 
                                  e.target.src='data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
                                  e.target.style.display = 'none';
                                }}
                              />
                            </div>
                          </div>
                        ) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            );
          })() : (
            <p className="text-gray-500">No coins found in this submission.</p>
          )}
        </div>
      </div>

      {/* Image Viewer Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-4xl w-full">
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300"
            >
              <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <img
              src={selectedImage.startsWith('http') ? selectedImage : images.getImage(selectedImage)}
              alt="Full size"
              className="w-full h-auto rounded-lg bg-white"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
}
